import { ChevronRight, ExternalLink, FolderTree } from "lucide-react";
import type {
  Product,
  ProductVariant,
} from "@sakura-tech-lab/dashboard-api";
import type { CategoryTreeNode } from "@/services/category.service";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ShimmerText } from "@/components/ui/shimmer-text";
import {
  getIngestionProgress,
  hasEnrichedTitle,
  type IngestionProgress,
} from "@/lib/ingestion-progress";
import { cn } from "@/lib/utils";
import {
  formatPrice,
  getProductTitle,
  getRepresentativeOffer,
  humanizeKey,
  resolveCategoryPaths,
} from "./product-detail-utils";

interface ProductSummaryPanelProps {
  readonly product: Product;
  readonly selectedVariant: ProductVariant | null;
  readonly categoryTree: readonly CategoryTreeNode[];
  readonly variantLabels: ReadonlyMap<string, string>;
  readonly language: string;
  readonly languages: readonly string[];
  readonly onLanguageChange: (language: string) => void;
  readonly onVariantChange: (variantId: string) => void;
}

export function ProductSummaryPanel({
  product,
  selectedVariant,
  categoryTree,
  variantLabels,
  language,
  languages,
  onLanguageChange,
  onVariantChange,
}: ProductSummaryPanelProps) {
  const variants = product.variants ?? [];
  const assignedCategories = product.categoryDetails?.length
    ? product.categoryDetails
    : product.categories;
  const categories = resolveCategoryPaths(assignedCategories, categoryTree, language);
  const ingestionProgress = getIngestionProgress(product);
  const titleStillProcessing =
    ingestionProgress.isTracked &&
    !hasEnrichedTitle(product) &&
    !ingestionProgress.isComplete &&
    !ingestionProgress.isFailed &&
    !ingestionProgress.isPartial;
  const representativeOffer = getRepresentativeOffer(product, selectedVariant);
  const title = getProductTitle(product, selectedVariant, language);
  const price = representativeOffer?.price ?? (!selectedVariant ? product.price : undefined);
  const discount =
    representativeOffer?.priceDiscount ?? (!selectedVariant ? product.priceDiscount : undefined);
  const currentPrice = formatPrice(discount) ?? formatPrice(price);
  const originalPrice = formatPrice(discount) ? formatPrice(price) : null;
  const inStock = representativeOffer?.inStock ?? (!selectedVariant ? product.inStock : null);
  const isPublished =
    representativeOffer?.isPublished ?? (!selectedVariant ? product.isPublished : null);
  const colour = selectedVariant ? selectedVariant.colour : product.colour;
  const externalUrl = representativeOffer?.url || (!selectedVariant ? product.url : "");
  const brandName = product.brand?.name?.trim();

  return (
    <Card className="min-w-0">
      <CardContent className="space-y-5 p-5 sm:p-6">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
          {categories.length > 0 ? (
            <nav
              aria-label="Assigned categories"
              className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
            >
              <FolderTree aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
              {categories.map((category, index) => (
                <span
                  key={`${category.id}-${category.names.join("/")}`}
                  className="inline-flex min-w-0 flex-wrap items-center gap-1.5 text-xs"
                >
                  {index > 0 ? (
                    <span aria-hidden="true" className="text-muted-foreground/40">•</span>
                  ) : null}
                  {category.names.map((name, level) => (
                    <span key={`${level}-${name}`} className="inline-flex min-w-0 items-center gap-1.5">
                      {level > 0 ? (
                        <ChevronRight
                          aria-hidden="true"
                          className="h-3 w-3 shrink-0 text-muted-foreground/60"
                        />
                      ) : null}
                      <span
                        className={cn(
                          "break-words",
                          level === category.names.length - 1
                            ? "font-medium text-foreground"
                            : "text-muted-foreground",
                        )}
                      >
                        {name}
                      </span>
                    </span>
                  ))}
                </span>
              ))}
            </nav>
          ) : (
            <span className="text-xs text-muted-foreground">Uncategorized</span>
          )}

          {languages.length > 1 ? (
            <Select value={language} onValueChange={onLanguageChange}>
              <SelectTrigger className="h-9 w-32" aria-label="Product language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((value) => (
                  <SelectItem key={value} value={value}>
                    {value.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>

        <div className="min-w-0 space-y-2">
          <h1 className="break-words text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
            <ShimmerText
              text={title}
              active={titleStillProcessing}
              tooltip="AI is still enriching this product — showing the supplier's original title"
            />
          </h1>
          {brandName ? <p className="text-sm font-medium text-muted-foreground">{brandName}</p> : null}
        </div>

        {variants.length > 1 ? (
          <fieldset className="min-w-0 space-y-2">
            <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Variants
            </legend>
            <div className="flex min-w-0 flex-wrap gap-2">
              {variants.map((variant, index) => {
                const selected = selectedVariant?.id === variant.id;
                return (
                  <Button
                    key={variant.id}
                    type="button"
                    size="sm"
                    variant={selected ? "default" : "outline"}
                    className="h-auto min-h-9 max-w-full whitespace-normal px-3 py-1.5 text-left"
                    aria-pressed={selected}
                    onClick={() => onVariantChange(variant.id)}
                  >
                    {variantLabels.get(variant.id) ?? `Variant ${index + 1}`}
                  </Button>
                );
              })}
            </div>
          </fieldset>
        ) : null}

        <div className="flex flex-wrap items-center gap-2" aria-label="Product status">
          {inStock !== null ? <StockBadge inStock={inStock} /> : null}
          {isPublished !== null ? (
            <Badge
              variant="outline"
              className={cn(
                isPublished
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "border-muted-foreground/20 bg-muted text-muted-foreground",
              )}
            >
              {isPublished ? "Published" : "Unpublished"}
            </Badge>
          ) : null}
          <ProcessBadge progress={ingestionProgress} />
          {product.moderation?.status ? <ModerationBadge status={product.moderation.status} /> : null}
          {product.productLabel ? <Badge variant="secondary">{product.productLabel}</Badge> : null}
        </div>

        {currentPrice || colour?.primary || colour?.secondary ? <Separator /> : null}

        {currentPrice ? (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-2xl font-bold text-foreground">{currentPrice}</span>
            {originalPrice && originalPrice !== currentPrice ? (
              <span className="text-sm text-muted-foreground line-through">{originalPrice}</span>
            ) : null}
          </div>
        ) : null}

        {colour?.primary || colour?.secondary ? (
          <div className="flex flex-wrap gap-2">
            {colour.primary ? <ColourChip label="Primary" value={colour.primary} /> : null}
            {colour.secondary ? <ColourChip label="Secondary" value={colour.secondary} /> : null}
          </div>
        ) : null}

        {product.caption ? (
          <p className="text-sm leading-relaxed text-muted-foreground">{product.caption}</p>
        ) : null}

        {externalUrl ? (
          <Button asChild variant="outline" className="w-full sm:w-auto">
            <a href={externalUrl} target="_blank" rel="noreferrer">
              View external listing
              <ExternalLink aria-hidden="true" />
            </a>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StockBadge({ inStock }: { readonly inStock: boolean }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        inStock
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
          : "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
      )}
    >
      {inStock ? "In stock" : "Out of stock"}
    </Badge>
  );
}

function ProcessBadge({ progress }: { readonly progress: IngestionProgress }) {
  const label = !progress.isTracked
    ? "Not tracked"
    : progress.isComplete
    ? "Completed"
    : progress.isFailed
      ? "Failed"
      : progress.isPartial
        ? "Partial"
        : `${progress.currentStage?.label ?? "Processing"} · ${progress.completedCount}/${progress.totalCount}`;

  return (
    <Badge
      variant="outline"
      title={progress.currentStage ? `Current stage: ${progress.currentStage.label}` : undefined}
      className={cn(
        progress.isTracked &&
          !progress.isComplete &&
          !progress.isFailed &&
          !progress.isPartial &&
          "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
        (progress.isFailed || progress.isPartial) &&
          "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
        progress.isComplete &&
          "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
        !progress.isTracked && "border-muted-foreground/20 bg-muted text-muted-foreground",
      )}
    >
      {label}
    </Badge>
  );
}

function ModerationBadge({ status }: { readonly status: string }) {
  const normalized = status.toLowerCase();
  return (
    <Badge
      variant="outline"
      className={cn(
        normalized === "approved" &&
          "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
        normalized === "flagged" &&
          "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
      )}
    >
      Moderation: {humanizeKey(status)}
    </Badge>
  );
}

function ColourChip({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs">
      <span
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 rounded-full border"
        style={{ backgroundColor: value }}
      />
      <span className="text-muted-foreground">{label}</span>
      <span className="break-words font-medium text-foreground">{value}</span>
    </span>
  );
}
